import React, { useMemo } from 'react';
import { Alert, Checkbox, Col, DatePicker, Empty, Input, InputNumber, Progress, Row, Tag } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { buildSatisfiedConditionChanges } from './serviceConditionHelpers';

function CustomerServiceConditionFields({ item, onChangeItem }) {
    const conditions = useMemo(() => item?.condition_values || [], [item]);
    const visibleConditions = useMemo(() => conditions.filter(condition => {
        const visibleWhen = condition.config?.visible_when;
        if (!visibleWhen?.condition_code) return true;
        const dependency = conditions.find(row => row.code === visibleWhen.condition_code);
        return !!dependency?.is_satisfied === !!visibleWhen.is_satisfied;
    }), [conditions]);
    const completedCount = visibleConditions.filter(condition => condition.is_satisfied).length;
    const progressPercent = visibleConditions.length
        ? Math.round((completedCount / visibleConditions.length) * 100)
        : 0;

    const changeCondition = (serviceConditionId, changes) => {
        const nextValues = conditions.map(condition => (
            Number(condition.service_condition_id) === Number(serviceConditionId)
                ? { ...condition, ...changes }
                : condition
        ));

        onChangeItem('condition_values', nextValues, item.id);
    };

    const handleSatisfiedChange = (condition, checked) => {
        changeCondition(
            condition.service_condition_id,
            buildSatisfiedConditionChanges(condition, checked, moment().format('YYYY-MM-DD'))
        );
    };

    const styles = {
        wrapper: {
            marginTop: 16,
            marginBottom: 12,
            background: '#fff',
            border: '1px solid #f0f0f0',
            borderRadius: 16,
            overflow: 'hidden',
        },
        header: {
            padding: '18px 20px 14px',
            background: 'linear-gradient(135deg, rgb(29 78 216 / 2%) 0%, rgb(37 99 235 / 10%) 45%, rgb(59 130 246 / 7%) 100%)',
            borderBottom: '1px solid #f0f0f0',
        },
        headerTop: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
        },
        titleWrap: { display: 'flex', alignItems: 'center', gap: 10 },
        titleIcon: {
            width: 34,
            height: 34,
            borderRadius: 10,
            background: '#e6f4ff',
            color: '#1677ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
        },
        title: { fontSize: 17, fontWeight: 700, color: '#262626' },
        subtitle: { marginTop: 4, color: '#3e3d3d', fontSize: 13, lineHeight: 1.6 },
        body: { padding: 20 },
        progressBox: {
            marginBottom: 20,
            padding: '14px 16px',
            background: '#fafcff',
            border: '1px solid #e6f4ff',
            borderRadius: 12,
        },
        fieldBox: {
            height: '100%',
            padding: 14,
            border: '1px solid #f0f0f0',
            borderRadius: 12,
            background: '#fff',
        },
        description: { marginTop: 6, color: '#595959', fontSize: 12, lineHeight: 1.5 },
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.header}>
                <div style={styles.headerTop}>
                    <div>
                        <div style={styles.titleWrap}>
                            <div style={styles.titleIcon}><CheckCircleOutlined /></div>
                            <div style={styles.title}>Thông tin phục vụ tính hoa hồng</div>
                        </div>
                        <div style={styles.subtitle}>
                            Điều kiện được tải theo cấu hình của nhóm dịch vụ đang chọn.
                        </div>
                    </div>
                    <Tag color="blue" style={{ marginRight: 0, padding: '4px 10px' }}>
                        Nhóm dịch vụ: {item?.service_group_name || 'Chưa cấu hình'}
                    </Tag>
                </div>
            </div>

            <div style={styles.body}>
                {conditions.length > 0 ? (
                    <>
                        <div style={styles.progressBox}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ fontWeight: 600, color: '#1d39c4' }}>
                                    Tiến độ hoàn thiện thông tin
                                </span>
                                <span>{completedCount}/{visibleConditions.length} mục</span>
                            </div>
                            <Progress percent={progressPercent} showInfo={false} />
                        </div>

                        <Row gutter={[16, 16]} className="mb-3">
                            {visibleConditions.map(condition => (
                                <Col xs={24} md={12} key={condition.service_condition_id}>
                                    <div style={styles.fieldBox}>
                                        {condition.input_type === 'boolean' ? (
                                            <>
                                                <Checkbox
                                                    checked={!!condition.is_satisfied}
                                                    disabled={!!condition.read_only}
                                                    onChange={event => handleSatisfiedChange(
                                                        condition,
                                                        event.target.checked
                                                    )}
                                                >
                                                    <span style={{ fontWeight: 500 }}>{condition.name}</span>
                                                </Checkbox>
                                                {condition.read_only ? (
                                                    <Tag color="cyan" style={{ marginLeft: 8 }}>
                                                        Tự động từ ảnh ({condition.evidence_count || 0})
                                                    </Tag>
                                                ) : null}
                                            </>
                                        ) : condition.input_type === 'number' ? (
                                            <>
                                                <div style={{ fontWeight: 500, marginBottom: 8 }}>{condition.name}</div>
                                                <InputNumber
                                                    min={condition.config?.min ?? 0}
                                                    max={condition.config?.max_source === 'customer_service.quantity' ? Number(item.quantity) || 0 : condition.config?.max}
                                                    value={condition.value_number}
                                                    placeholder="Nhập số lượng đã thực hiện"
                                                    style={{ width: '100%' }}
                                                    disabled={!!condition.read_only}
                                                    onChange={value => changeCondition(condition.service_condition_id, {
                                                        value_number: value,
                                                        is_satisfied: Number(value) > 0,
                                                        metadata: { ...(condition.metadata || {}), value_number: value },
                                                    })}
                                                />
                                            </>
                                        ) : (
                                            <Alert
                                                type="warning"
                                                message={`Chưa hỗ trợ loại dữ liệu ${condition.input_type}`}
                                            />
                                        )}
                                        {/* {condition.required ? <Tag color="red" style={{ marginLeft: 8 }}>Bắt buộc</Tag> : null} */}
                                        {condition.description ? (
                                            <div style={styles.description}>{condition.description}</div>
                                        ) : null}
                                        {condition.require_date ? (
                                            <DatePicker
                                                value={condition.occurred_at ? moment(condition.occurred_at) : null}
                                                format="DD/MM/YYYY"
                                                placeholder="Chọn ngày phát sinh"
                                                disabled={!condition.is_satisfied || !!condition.read_only}
                                                style={{ width: '100%', marginTop: 10 }}
                                                onChange={date => changeCondition(
                                                    condition.service_condition_id,
                                                    { occurred_at: date ? date.format('YYYY-MM-DD') : null }
                                                )}
                                            />
                                        ) : null}
                                        {/* <Input.TextArea
                                            value={condition.note || ''}
                                            rows={2}
                                            placeholder="Ghi chú (nếu có)"
                                            style={{ marginTop: 10 }}
                                            disabled={!!condition.read_only}
                                            onChange={event => changeCondition(
                                                condition.service_condition_id,
                                                { note: event.target.value }
                                            )}
                                        /> */}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </>
                ) : (
                    <Alert
                        type="info"
                        message="Nhóm dịch vụ chưa có điều kiện chuyên môn đang áp dụng."
                        description={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    />
                )}

            </div>
        </div>
    );
}

export default React.memo(CustomerServiceConditionFields);
